import styled, { keyframes, css } from 'styled-components';
import { hoverCapable } from '../styles/hover';
import { reducedMotion } from '../styles/motion';

/**
 * Estilos del banner de sincronización y del aviso de imágenes perdidas.
 *
 * Viven aparte para que los dos avisos se vean igual sin importarse entre sí
 * (spec 2026-10-01-photo-outbox-identity § 5.4). Se pintan apilados dentro de
 * `BannerStack`, que es el único elemento fijo en pantalla.
 */

export type BannerVariant = 'offline' | 'pending' | 'syncing' | 'failed';

// ── Animations ──
const slideIn = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateY(0);    opacity: 1; }
  to   { transform: translateY(100%); opacity: 0; }
`;

/**
 * Columna fija abajo a la izquierda: el aviso de imágenes perdidas encima y el
 * banner de sincronización debajo. Antes el banner era `fixed` por sí mismo, y
 * dos avisos fijos en el mismo sitio se tapaban.
 */
export const BannerStack = styled.div`
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  max-width: min(380px, calc(100vw - 32px));
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

export const Snackbar = styled.div<{
  $variant: BannerVariant;
  $exiting: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  max-width: 100%;

  background-color: ${({ $variant }) =>
    $variant === 'offline'
      ? '#dc2626'
      : $variant === 'syncing'
        ? '#d97706'
        : $variant === 'failed'
          ? '#ea580c'
          : '#2563eb'};

  animation: ${({ $exiting }) =>
    $exiting
      ? css`${slideOut} 0.25s ease-in forwards`
      : css`${slideIn} 0.3s ease-out`};
`;

// El punto de «sincronizando» late 3 veces al empezar y se queda fijo (acaba
// en opacidad 1); el texto del aviso ya dice que sigue sincronizando. En bucle
// infinito, un vaciado largo tenía el WebView del APK dibujando a 60 fps, y
// aquí además con la pantalla forzada encendida por `withScreenAwake`. Al
// salir de `syncing` y volver, la animación arranca de nuevo. Ver
// `src/styles/motion.ts`.
export const StatusDot = styled.span<{
  $variant: BannerVariant;
}>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $variant }) =>
    $variant === 'offline'
      ? '#fca5a5'
      : $variant === 'syncing'
        ? '#fcd34d'
        : $variant === 'failed'
          ? '#fed7aa'
          : '#93c5fd'};

  ${({ $variant }) =>
    $variant === 'syncing' &&
    css`
      animation: ${keyframes`
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      `} 1.2s ease-in-out 3;

      ${reducedMotion} {
        animation: none;
      }
    `}
`;

export const Message = styled.span`
  flex: 1;
  line-height: 1.3;
`;

// Hover dentro de `hoverCapable`: en la tablet el :hover se queda pegado tras
// el toque (regla .claude/rules/hover-tactil.md).
export const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  ${hoverCapable} {
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 16px;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;
  ${hoverCapable} {
    &:hover {
      color: white;
    }
  }
`;
